import { ReactComponent as ExclamationIcon } from 'assets/red-info.svg';
import classnames from 'classnames';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import BasicModalTemplate from '../../../../../common/modals/BasicModalTemplate';
import { ModalInstanceProps } from '../../../../../common/modals/interfaces';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import {
  isBilling2023Subscription,
  SubscriptionModel,
} from '@mm/state/model/SubscriptionModel';
import modalActions, {
  ManagePagesModalParams,
} from '@mm/state/modules/modalsState/actions';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import './ManagePagesModal.pcss';

type OuterProps = ManagePagesModalParams & ModalInstanceProps;

type InnerProps = ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const ManagePagesModal = (props: Props) => {
  const {
    subscription,
    plan,
    closeModal,
    pagesCount,
    openAddMorePagesTeamModal,
    openConfirmManagePagesModal,
    facebookPageId,
  } = props;
  const [additionalPages, setAdditionalPages] = useState(
    subscription.extraPages,
  );
  const company = useSelector(activeCompanySelector);
  const [makingRequest, setMakingRequest] = useState(false);
  const value = additionalPages || 0;
  const amount = value - (subscription.extraPages as number);
  const addingPages = amount > 0;
  const difference =
    pagesCount - ((subscription.pagesLimit as number) + amount);
  const tooLittlePages = difference > 0;
  const decrementClasses = classnames('manage-pages-modal__decrement', {
    'manage-pages-modal__decrement-disabled': value === 0,
  });

  const isNewBillingSubscription =
    subscription && isBilling2023Subscription(subscription);

  const subscriptionCRUDActions = useSubscriptionCRUDActions();

  const addExtraPagesToTeam = (subscriptionId: number, pagesCount: number) =>
    subscriptionCRUDActions.addExtraPagesToTeam(
      subscriptionId,
      company ? company.id : -1,
      pagesCount,
    );

  const onConfirm = async () => {
    if (addingPages) {
      closeModal();
      openAddMorePagesTeamModal(subscription, plan, amount, facebookPageId);
    } else {
      setMakingRequest(true);
      await addExtraPagesToTeam(subscription.id, amount);
      if (facebookPageId) {
        subscriptionCRUDActions.getCurrentSubscription(
          facebookPageId,
          false,
          subscription.id,
          isNewBillingSubscription,
        );
      }
      closeModal();
      openConfirmManagePagesModal(subscription, -amount);
    }
  };

  let notificationClass;

  if (amount >= 0) {
    notificationClass = 'manage-pages-modal__notification-add';
  } else if (tooLittlePages) {
    notificationClass = 'manage-pages-modal__notification-remove-bad';
  } else {
    notificationClass = 'manage-pages-modal__notification-remove';
  }

  return (
    <BasicModalTemplate
      className="manage-pages-modal"
      withMonkeyBackground={false}
      iconClass="manage-pages-modal__close-icon"
    >
      <div className="manage-pages-modal__body">
        <div className="manage-pages-modal__header-text">
          Manage Additional Page Slots
        </div>
        <div className="manage-pages-modal__subheader-text">
          Please review the number of additional page slots
        </div>
        <div className="manage-pages-modal__management">
          <div className="manage-pages-modal__controls">
            <span
              className={decrementClasses}
              onClick={() => value > 0 && setAdditionalPages(value - 1)}
            >
              –
            </span>
            <span className="manage-pages-modal__value">{value}</span>
            <span
              className="manage-pages-modal__increment"
              onClick={() => setAdditionalPages(value + 1)}
            >
              +
            </span>
          </div>
          <div className={notificationClass}>
            {tooLittlePages && (
              <>
                <ExclamationIcon className="manage-pages-modal__red-info-icon" />
                <p className="manage-pages-modal__notification-text">
                  {difference} of the page slots you wish to deactivate{' '}
                  {difference > 1 ? 'are' : 'is'} occupied. Remove {difference}{' '}
                  {pluralize('page', difference)} from your subscription before
                  deactivating.
                </p>
              </>
            )}
            {amount < 0 && !tooLittlePages && (
              <p className="manage-pages-modal__notification-text">
                {-amount} page {pluralize('slot', -amount)} will be disabled
              </p>
            )}
            {amount > 0 && (
              <p className="manage-pages-modal__notification-text">
                {amount} page {pluralize('slot', amount)} will be added
              </p>
            )}
            {amount === 0 && (
              <p className="manage-pages-modal__notification-text">
                Current number of extra page slots
              </p>
            )}
          </div>
        </div>
        <div className="manage-pages-modal__buttons">
          <div
            onClick={() => closeModal()}
            className="manage-pages-modal__cancel-button"
          >
            Cancel
          </div>
          <PrimaryButton
            disabled={tooLittlePages || makingRequest || amount === 0}
            onClick={onConfirm}
            className="manage-pages-modal__confirm-button"
          >
            Confirm Changes
          </PrimaryButton>
        </div>
      </div>
    </BasicModalTemplate>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openConfirmManagePagesModal: (
    subscription: SubscriptionModel,
    pagesCount: number,
  ) =>
    dispatch(
      modalActions.openConfirmManagePagesModal({
        subscription,
        pagesCount,
      }),
    ),
  openAddMorePagesTeamModal: (
    subscription: SubscriptionModel,
    plan: EnhancedPlan,
    pagesCount: number,
    facebookPageId?: number,
  ) =>
    dispatch(
      modalActions.openAddMorePagesTeamModal({
        subscription,
        plan,
        pagesCount,
        facebookPageId,
      }),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(null, mapDispatchToProps),
);

export default enhance(ManagePagesModal);
